export type GCSUploadOptions = {
  url: string;
  fields: Record<string, string>;
};

class GCSUploadError extends Error {
  detail: string;
  status: number;

  constructor({ detail, status }: { detail: string; status: number }, message?: string) {
    super(message);
    this.name = 'GCSUploadError';
    this.detail = detail;
    this.status = status;
  }
}

/**
 * Uploads a file to Google Cloud Storage using a signed POST policy.
 *
 * Ported from the Ember `utils/uploads/gcs` helper. The EU datacenter uploads
 * to GCS; see `getUploadFn` for how the target is chosen.
 */
export async function uploadToGCS(
  file: File,
  json: GCSUploadOptions,
  options: { processData?: (data: FormData) => void } = {},
): Promise<{ key: string }> {
  const data = new FormData();

  for (const key of Object.keys(json.fields)) {
    data.append(key, json.fields[key]);
  }

  options.processData?.(data);
  data.append('file', file);

  const response = await fetch(json.url, { method: 'POST', body: data });
  const text = await response.text();
  const xml = new DOMParser().parseFromString(text, 'text/xml');

  if (response.ok) {
    // GCS re-encodes filenames after upload, so we encodeURI the result to keep
    // our expectation aligned with what GCS stored (e.g. a space we encoded as
    // %20 would otherwise become %2520 once GCS re-encodes the % as %25).
    return { key: encodeURI(`${json.url}${json.fields.key}`) };
  }

  const detail =
    xml.getElementsByTagName('Message')[0]?.textContent ??
    'Something went wrong while uploading to Google Cloud Storage.';
  throw new GCSUploadError({ detail, status: response.status }, text);
}
